import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "d-flex align-center"
}
const _hoisted_2 = { class: "text-subtitle-2 p-0" }

import { computed, onMounted, watch } from 'vue';
    import { useRouter, useRoute } from 'vue-router';
    import { useI18n } from 'vue-i18n';
    import { useDisplay } from 'vuetify';
    import type { BreadcrumbItem } from '@/store/modules/breadcrumb';
    import { InternalBreadcrumbItem } from 'vuetify/lib/components/VBreadcrumbs/VBreadcrumbs';
    import { useStore } from 'vuex';
    import AQButton from '../shared/forms/inputs/AQButton.vue';

    
export default /*@__PURE__*/_defineComponent({
  __name: 'Breadcrumbs',
  emits: ["send-action"],
  setup(__props, { emit: __emit }) {

    const emit = __emit;

    const store = useStore();
    const route = useRoute();
    const breadcrumbs = computed(() => store.getters.breadcrumbs);
    const displayBreadcrumb = computed(() => store.getters.displayBreadcrumb);
    const client = computed(() => store.getters.client);
    const site = computed(() => store.getters.site);
    const workOrder = computed(() => store.getters.workOrder);
    const woTemplate = computed(() => store.getters.woTemplate);
    watch(
        () => client.value,
        (newClient) => {
            if (newClient?.id.length && route.params.clientId) {
                buildFromRoute();
            }
        },
        { deep: true },
    );
    const router = useRouter();
    const { t } = useI18n();
    const display = useDisplay();

    const allRoutes = router.getRoutes();
    const mappedItems = computed(() => {
        if (!breadcrumbs.value?.length) return [];
        return breadcrumbs.value.map((item: BreadcrumbItem, index: number) => {
            let toRoute = item.to;
            if (!toRoute && item.name && !item.disabled && index < breadcrumbs.value.length - 1) {
                const matchedRoute = allRoutes.find((r) => r.name === item.name);
                if (matchedRoute) {
                    const requiredParams: Record<string, string> = {};
                    const paramNames =
                        matchedRoute.path.match(/:(\w+)/g)?.map((p) => p.slice(1)) || [];
                    paramNames.forEach((paramName) => {
                        if (route.params[paramName]) {
                            requiredParams[paramName] = route.params[paramName] as string;
                        }
                    });
                    toRoute = { name: item.name, params: requiredParams };
                }
            }
            return { ...item, title: item.text, to: toRoute };
        });
    });

    const emptyBreadcrumbs = () => store.dispatch('emptyBreadcrumbs');
    const pushBreadcrumb = (breadcrumb: BreadcrumbItem) => {
        if (displayBreadcrumb.value) {
            store.dispatch('pushBreadcrumb', breadcrumb);
        }
    };

    const CRM_BREADCRUMBS: Record<string, { name: string; text: string }> = {
        'crm': { name: 'crm', text: 'crm.pageName' },
        'dashboard': { name: 'crm-dashboard', text: 'crm.dashboard.navTitle' },
        'opportunities': { name: 'crm-opportunities', text: 'crm.opportunity.navTitle' },
        'all-activities': { name: 'crm-all-activities', text: 'crm.allActivities.navTitle' },
        'access': { name: 'crm-access', text: 'crm.crmAccess.navTitle' },
        'configuration': { name: 'crm-configuration', text: 'crm.configurationCrm.navTitle' },
        'advanced-config': { name: 'crm-advanced-config', text: 'crm.advancedConfig.navTitle' },
        ':id': { name: 'crm-opportunity-detail', text: 'crm.opportunityDetail.navTitle' },
    };

    const buildFromRoute = () => {
        emptyBreadcrumbs();
        const paths = route.matched[route.matched.length - 1];
        if (!paths) return;
        const currentPath = paths.path.split('/');
        currentPath.shift();
        currentPath.forEach((routeSegment) => {
            if (currentPath[0] === 'crm') {
                const crumb = CRM_BREADCRUMBS[routeSegment];
                if (crumb) {
                    pushBreadcrumb(crumb);
                }
                return;
            }
            switch (routeSegment) {
                case 'work-order-templates':
                    pushBreadcrumb({
                        name: 'work-order-templates',
                        text: 'services.workOrdersTemplate',
                    });
                    break;
                case ':woTemplateId':
                    pushBreadcrumb({
                        name: 'siteEditTemplate',
                        text: `${woTemplate.value.title}`,
                    });
                    break;
                case 'edit-template':
                    pushBreadcrumb({
                        name: 'siteEditTemplate',
                        text: t('servicesPage.editTemplate'),
                    });
                    break;
                case 'create-template':
                    pushBreadcrumb({
                        name: 'create-template',
                        text: t('servicesPage.createTemplate'),
                    });
                    break;
                case 'billing':
                    pushBreadcrumb({
                        name: 'administration-management-billingRegister',
                        text: 'administration.billing',
                    });
                    break;
                case 'services':
                    pushBreadcrumb({
                        name: 'services',
                        text: 'servicesPage.services',
                    });
                    break;
                case 'services-billing-field-services':
                    pushBreadcrumb({
                        name: 'field-services',
                        text: 'servicesPage.fieldServicebilling',
                    });
                    break;
                case 'laboratory':
                    if (currentPath[0] === 'administration') {
                        return pushBreadcrumb({
                            name: 'services-billing-laboratory',
                            text: 'services.billingLaboratory',
                        });
                    }
                    pushBreadcrumb({
                        name: 'laboratory',
                        text: `pageToDisplay.${routeSegment}`,
                    });
                    break;
                // Common case
                case 'work-orders':
                    if (currentPath.includes('laboratory')) {
                        pushBreadcrumb({
                            name: 'work-orders',
                            text: 'laboratory.labWorkOrders',
                        });
                    }
                    if (currentPath.includes('services')) {
                        pushBreadcrumb({
                            name: 'work-orders',
                            text: 'services.workOrders',
                        });
                    }
                    if (currentPath.includes('sites')) {
                        pushBreadcrumb({
                            name: 'siteWorkOrders',
                            text: 'sitePage.workOrders',
                        });
                    }
                    break;
                // Case laboratory main
                case 'lab-analysis-requests':
                    pushBreadcrumb({
                        name: 'analysis-requests',
                        text: 'laboratory.labAnalysisRequests',
                    });
                    break;
                case 'lab-sample-register':
                    pushBreadcrumb({
                        name: 'samples',
                        text: 'laboratory.samplesRegister',
                    });
                    break;
                case 'lab-reports':
                    pushBreadcrumb({
                        name: 'reports',
                        text: 'laboratory.reports',
                    });
                    break;
                case 'lab-data-transfer':
                    pushBreadcrumb({
                        name: 'data-transfer',
                        text: 'laboratory.dataTransfer',
                    });
                    break;
                case 'lab-xml-reports':
                    pushBreadcrumb({
                        name: 'xml-report',
                        text: 'laboratory.xmlReport',
                    });
                    break;
                // Case administration main
                case 'configuration':
                case 'advancedConfiguration':
                case 'aquassistBilling':
                case 'trends':
                case 'documentLibrary':
                case 'networkTypes':
                case 'componentTypes':
                case 'analysisTypes':
                    pushBreadcrumb({
                        text: `administration.${routeSegment}`,
                    });
                    break;
                case 'analyses':
                    pushBreadcrumb({
                        name: 'administration-configuration-analyses',
                        text: 'administration.analyses',
                    });
                    break;
                case 'categories':
                    pushBreadcrumb({
                        name: 'administration-configuration-analyses-categories',
                        text: 'administration.analysesCategories',
                    });
                    break;
                case 'types':
                    pushBreadcrumb({
                        name: 'administration-configuration-analyses-types',
                        text: 'administration.analysesTypes',
                    });
                    break;
                case 'management':
                    pushBreadcrumb({
                        name: 'administration-management',
                        text: 'administration.management',
                    });
                    break;
                case 'users':
                    pushBreadcrumb({
                        name: 'administration-management-users',
                        text: 'administration.users',
                    });
                    break;
                case ':id':
                    break;
                case 'access-groups':
                    pushBreadcrumb({
                        name: 'administration-management-accessGroups',
                        text: 'administration.accessGroups',
                    });
                    break;
                case 'dynamic-comments':
                    pushBreadcrumb({
                        name: 'administration-management-automaticSentences',
                        text: 'administration.automaticSentences',
                    });
                    break;
                case 'default-cards':
                    pushBreadcrumb({
                        name: 'administration-management-defaultAnalyses',
                        text: 'administration.defaultAnalyses',
                    });
                    break;
                case 'standard-&-limits':
                    pushBreadcrumb({
                        name: 'administration-management-standardFamilies',
                        text: 'administration.standardFamilies',
                    });
                    break;
                case 'contracts':
                    pushBreadcrumb({
                        name: 'administration-management-contracts',
                        text: 'administration.contracts',
                    });
                    break;
                case 'add-ons':
                    pushBreadcrumb({
                        name: 'administration-management-features',
                        text: 'administration.addOns',
                    });
                    break;
                case 'features':
                    pushBreadcrumb({
                        name: 'administration-advance-configuration-features',
                        text: 'administration.features',
                    });
                    break;
                case 'billing-register':
                    pushBreadcrumb({
                        name: 'administration-configuration-billingRegister',
                        text: 'administration.billingRegister',
                    });
                    break;
                case 'info':
                    pushBreadcrumb({
                        text: currentPath.includes('clients')
                            ? `clientPage.${routeSegment}`
                            : `administration.${routeSegment}`,
                    });
                    break;
                case 'service-provider':
                    pushBreadcrumb({
                        name: 'administration-configuration-serviceProvider',
                        text: 'administration.companyInformation',
                    });
                    break;
                case 'service-providers':
                    pushBreadcrumb({
                        name: 'administration-advance-configuration-service-providers',
                        text: 'administration.serviceProviders',
                    });
                    break;
                case 'nodes':
                    pushBreadcrumb({
                        name: 'administration-advance-configuration-nodes',
                        text: 'administration.nodes',
                    });
                    break;
                case 'video-categories':
                    pushBreadcrumb({
                        name: 'administration-advance-configuration-video-categories',
                        text: 'administration.videoCategories',
                    });
                    break;
                case 'device-types':
                    pushBreadcrumb({
                        name: 'deviceTypes',
                        text: 'deviceAnalysisKeys.deviceTypes',
                    });
                    break;
                case 'device-analysis-keys':
                    pushBreadcrumb({
                        name: 'administration-advance-configuration-deviceAnalysisKeys',
                        text: 'administration.deviceAnalysisKeys',
                    });
                    break;
                //case Aquassist Billing
                case 'pricing-management':
                    pushBreadcrumb({
                        name: 'administration-configuration-pricingManagement',
                        text: 'administration.pricingManagement',
                    });
                    break;
                // case Clients
                case ':clientId':
                    pushBreadcrumb({
                        name: 'clientInfo',
                        text: `${client.value.name} [${client.value.accountNumber}]`,
                    });
                    break;
                case ':siteId':
                    pushBreadcrumb({
                        name: 'site-tree',
                        text: `${site.value.name} [${site.value.deliveryAccountNumber}]`,
                    });
                    break;
                case 'manageClientTeam':
                    pushBreadcrumb({
                        name: 'clientManageClientTeam',
                        text: 'clientPage.manageClientTeam',
                    });
                    break;
                case 'devices':
                    pushBreadcrumb({
                        name: 'siteDevices',
                        text: 'sitePage.devices',
                    });
                    break;
                case 'analysis-request':
                    pushBreadcrumb({
                        name: 'siteAnalysisRequest',
                        text: 'sitePage.analysisRequest',
                    });
                    break;
                case 'graphics':
                    pushBreadcrumb({
                        name: 'siteGraphics',
                        text: 'sitePage.graphics',
                    });
                    break;
                case 'cart':
                    pushBreadcrumb({
                        name: 'siteCart',
                        text: 'sitePage.cart',
                    });
                    break;
                case 'document':
                    pushBreadcrumb({
                        name: 'siteDocument',
                        text: 'sites.document.pageName',
                    });
                    break;
                case 'alerts':
                    pushBreadcrumb({
                        name: 'siteAlerts',
                        text: 'sitePage.alerts',
                    });
                    break;
                case 'mass-balance':
                    pushBreadcrumb({
                        name: 'siteMassBalance',
                        text: 'sitePage.massBalance',
                    });
                    break;
                case 'logs':
                    pushBreadcrumb({
                        name: 'siteLogs',
                        text: 'sitePage.logs',
                    });
                    break;
                // case notifications
                case 'notifications':
                    pushBreadcrumb({
                        name: 'notifications',
                        text: 'alertsNotifications.pageName',
                    });
                    break;
                // case teams
                case 'teams':
                    pushBreadcrumb({
                        name: 'teams',
                        text: 'teams.selectTeam',
                    });
                    break;
                // case 'products':
                case 'products':
                    pushBreadcrumb({
                        name: 'siteProducts',
                        text: 'sitePage.products',
                    });
                    break;
                case 'procedures':
                    if (currentPath.includes('administration')) {
                        pushBreadcrumb({
                            name: 'administration-configuration-procedures',
                            text: 'administration.procedures',
                        });
                    } else {
                        pushBreadcrumb({
                            name: 'siteProcedures',
                            text: 'sitePage.procedures',
                        });
                    }
                    break;
                case 'sites':
                    pushBreadcrumb({
                        name: 'clientSites',
                        text: 'pageToDisplay.sites',
                    });
                    break;
                // case Work Orders
                case ':workOrderId':
                    pushBreadcrumb({
                        name: 'workOrders',
                        text: `${workOrder.value.contractNumber}`,
                    });
                    break;
                default:
                    pushBreadcrumb({
                        name: routeSegment,
                        text: `pageToDisplay.${routeSegment}`,
                    });
                    break;
            }
        });
    };

    watch(route, () => {
        buildFromRoute();
    });

    onMounted(() => {
        buildFromRoute();
    });

    const renderItemText = (item: unknown): string => {
        const breadcrumbItem = item as InternalBreadcrumbItem & BreadcrumbItem;
        return breadcrumbItem.noTranslate ? breadcrumbItem.text : t(breadcrumbItem.text);
    };

    const handleItemClick = (item: unknown) => {
        const breadcrumbItem = item as InternalBreadcrumbItem & BreadcrumbItem;
        if (!breadcrumbItem.name && breadcrumbItem.action) {
            emit('send-action', breadcrumbItem.action);
        }
    };

return (_ctx: any,_cache: any) => {
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_breadcrumbs_item = _resolveComponent("v-breadcrumbs-item")!
  const _component_v_breadcrumbs = _resolveComponent("v-breadcrumbs")!

  return (displayBreadcrumb.value)
    ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
        _createVNode(AQButton, {
          variant: "text",
          basic: "",
          size: _unref(display).mdAndDown.value ? 'small' : 'default',
          height: _unref(display).mdAndDown.value ? 40 : 60,
          onClick: _cache[0] || (_cache[0] = ($event: any) => (_unref(router).go(-1)))
        }, {
          default: _withCtx(() => [
            _createVNode(_component_v_icon, { size: "large" }, {
              default: _withCtx(() => [...(_cache[1] || (_cache[1] = [
                _createTextVNode(" mdi-chevron-left ", -1)
              ]))]),
              _: 1
            })
          ]),
          _: 1
        }, 8, ["size", "height"]),
        _createVNode(_component_v_breadcrumbs, {
          items: mappedItems.value,
          divider: ">",
          size: "large"
        }, {
          item: _withCtx(({ item }) => [
            _createVNode(_component_v_breadcrumbs_item, {
              "active-class": "breadcrumb-active",
              to: item.to,
              disabled: item.disabled,
              onClick: ($event: any) => (handleItemClick(item))
            }, {
              default: _withCtx(() => [
                _createElementVNode("span", _hoisted_2, _toDisplayString(renderItemText(item)), 1)
              ]),
              _: 2
            }, 1032, ["to", "disabled", "onClick"])
          ]),
          _: 1
        }, 8, ["items"])
      ]))
    : _createCommentVNode("", true)
}
}

})